import React, { useState } from "react";

import PageTitle from "../../../layouts/PageTitle";
import { useGetNcsTransitionsQuery } from "../../../../store/appApi";
import {
  usePartnerCode,
  PartnerPicker,
  panelClass,
  fieldClass,
  btnGhost,
  Badge,
  TRANSITION_TONE,
  TRANSITION_TYPES,
  fmtDate,
} from "./config";
import { downloadWorkbook } from "../../../../services/netplusCustomerStatus";

const NcsTransitions = () => {
  const [partnerCode, setPartnerCode] = usePartnerCode();
  const [type, setType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  const { data, isFetching } = useGetNcsTransitionsQuery({
    partnerCode,
    type,
    from,
    to,
    page,
    limit: 100,
  });

  const rows = data?.transitions || [];
  const totalPages = data?.totalPages || 1;

  return (
    <>
      <PageTitle activeMenu="Transitions" motherMenu="Netplus" />

      <section className={panelClass}>
        <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 px-5 py-3">
          <div>
            <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
              Type
            </span>
            <select
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All types</option>
              {TRANSITION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
              From
            </span>
            <input
              type="date"
              className={`${fieldClass} w-40`}
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
              To
            </span>
            <input
              type="date"
              className={`${fieldClass} w-40`}
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <PartnerPicker value={partnerCode} onChange={setPartnerCode} />
            <button
              className={btnGhost}
              onClick={() => downloadWorkbook(partnerCode, { view: "transitions" })}
            >
              Export
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-center">From → To</th>
                <th className="px-4 py-3 text-center">Expiry move</th>
                <th className="px-4 py-3 text-center">Via</th>
                <th className="px-4 py-3 text-center">Days lapsed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    No transitions for <strong>{partnerCode}</strong>. Transitions
                    appear after a second, later-dated sheet is uploaded and the
                    diff runs. Check the partner selector and the Upload page.
                  </td>
                </tr>
              ) : (
                rows.map((t) => (
                  <tr key={t._id} className="hover:bg-cyan-50/30">
                    <td className="px-4 py-2.5 text-gray-500">{fmtDate(t.toDate)}</td>
                    <td className="px-4 py-2.5">
                      <Badge tone={TRANSITION_TONE[t.type]}>{t.type}</Badge>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-800">
                        {t.name || t.userId}
                      </div>
                      <div className="flex flex-wrap gap-x-1 text-xs text-gray-400">
                        <span className="whitespace-nowrap">m:{t.phone || "—"},</span>
                        <span className="whitespace-nowrap">id:{t.userId}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-center text-gray-500">
                      {(t.fromStatus || "—") + " → " + t.toStatus}
                    </td>
                    <td className="px-4 py-2.5 text-center text-xs text-gray-400">
                      {fmtDate(t.fromExpiryDate)} → {fmtDate(t.toExpiryDate)}
                    </td>
                    <td className="px-4 py-2.5 text-center text-xs text-gray-500">
                      {t.recoveredVia || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-center tabular-nums">
                      {t.daysLapsedAtEvent ?? "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-3 text-xs text-gray-500">
          <span>{data?.totalCount ?? 0} total</span>
          <div className="flex items-center gap-2">
            <button
              className={btnGhost}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              className={btnGhost}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default NcsTransitions;
